import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { useFormik } from "formik";
import styled from "styled-components";
import classNames from "classnames";
import { Dropdown } from "primereact/dropdown";
import { handlePostRequest } from "../../services/PostTemplate";

const DeviceManagerActionForm = ({ data, onCloseModal }) => {
  const [loading, setloading] = useState(false);
  const [loadingIcon, setloadingIcon] = useState("pi pi-save");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [otpLimitText, setOtpLimitText] = useState("");

  useEffect(() => {
    // Check localStorage on component mount to see if button should be disabled
    const clickData = JSON.parse(localStorage.getItem("dmOtpLimit")) || {};
    const { clicks, timestamp } = clickData;
    const now = new Date().getTime();

    if (clicks >= 4 && timestamp && now - timestamp < 120000) {
      setIsButtonDisabled(true);
      const remainingTime = 120000 - (now - timestamp);
      setTimeout(() => resetClicks(), remainingTime);
    } else {
      resetClicks();
    }
  }, []);

  const resetClicks = () => {
    setIsButtonDisabled(false);
    setOtpLimitText("");
    localStorage.removeItem("dmOtpLimit");
  };

  const handleButtonClick = () => {
    const clickData = JSON.parse(localStorage.getItem("dmOtpLimit")) || {
      clicks: 0,
      timestamp: 0,
    };
    let { clicks } = clickData;
    clicks += 1;

    if (clicks > 3) {
      setIsButtonDisabled(true);
      localStorage.setItem(
        "dmOtpLimit",
        JSON.stringify({ clicks, timestamp: new Date().getTime() })
      );
      setOtpLimitText("Too many wrong attemps! Please try after 2mins.");
      setTimeout(() => resetClicks(), 120000); // 2-minute timer
    } else {
      localStorage.setItem(
        "dmOtpLimit",
        JSON.stringify({ clicks, timestamp: clickData.timestamp })
      );
    }
  };

  const otpRefs = useRef([]);
  const addRef = (el) => {
    if (el && !otpRefs.current.includes(el)) {
      otpRefs.current.push(el);
    }
  };

  const handleInputChange = (e, index) => {
    formik.setFieldValue(`otp${index + 1}`, e.target.value);

    if (e.target.value.length === 1 && index < 4) {
      otpRefs.current[index + 1].focus();
    } else if (e.target.value === "" && e.keyCode === 8 && index > 0) {
      // keyCode 8 is for backspace
      otpRefs.current[index - 1].focus();
    }
  };

  const handleFocus = (e) => {
    // Set the cursor to the end
    const valueLength = e.target.value.length;
    e.target.setSelectionRange(valueLength, valueLength);
  };

  const handleKeyNavigation = (e, index) => {
    if (e.keyCode === 37 && index > 0) {
      // left arrow key
      otpRefs.current[index - 1].focus();
    } else if (e.keyCode === 39 && index < 3) {
      // right arrow key
      otpRefs.current[index + 1].focus();
    }
  };

  const approvalStatusOptions = [{ label: "ACTIVE", value: "ACTIVE" }];

  const formik = useFormik({
    initialValues: {
      otp1: "",
      otp2: "",
      otp3: "",
      otp4: "",
      otp5: "",
      approvalStatus: "",
    },
    validate: (values) => {
      let errors = {};
      if (!values.otp1 || !values.otp2 || !values.otp3 || !values.otp4) {
        errors.otp5 = "Please enter OTP.";
      }
      if (!values.approvalStatus) {
        errors.approvalStatus = "Approval status is required.";
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        setloading(true);
        setloadingIcon("pi pi-spin pi-spinner");
        const OTP =
          values.otp1 + values.otp2 + values.otp3 + values.otp4 + values.otp5;
        const updatedData = {
          ...data,
          OTP,
          ApprovalStatus: values.approvalStatus,
        };
        await handlePostRequest(
          updatedData,
          "zbizgateway/accountMaintainence/deviceVerification"
        );
        setloading(false);
        setloadingIcon("pi pi-save");
        formik.resetForm();
        onCloseModal();
      } catch (err) {
        setloading(false);
        setloadingIcon("pi pi-save");
        formik.resetForm();
      }
    },
  });

  const isFormFieldValid = (name) =>
    !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    );
  };

  return (
    <div className="card Card__Round">
      <div className="Form__Header">
        <h1> Verify OTP </h1>
      </div>
      <form onSubmit={formik.handleSubmit} className="p-fluid p-mt-2">
        <div className="p-mt-4 p-mb-4">
          <div>
            <p>
              Please enter the OTP code sent to your registered Mobile Number /
              Email Address. <br />
            </p>
            <div>
              {[1, 2, 3, 4, 5].map((_, index) => (
                <Input
                  key={index}
                  maxLength="1"
                  ref={addRef}
                  onChange={(e) => handleInputChange(e, index)}
                  onKeyDown={(e) => handleKeyNavigation(e, index)}
                  onFocus={handleFocus}
                  value={formik.values[`otp${index + 1}`]}
                  name={`otp${index + 1}`}
                  type="text"
                />
              ))}
              {getFormErrorMessage("otp5")}
            </div>
            <small
              className="p-mt-4"
              style={{ display: "block", color: "red" }}
            >
              {otpLimitText && otpLimitText}
            </small>
          </div>
        </div>
        <div className="p-field p-col-12 p-md-3">
          <div className="p-field">
            <label
              htmlFor="approvalStatus"
              className={classNames(
                { "p-error": isFormFieldValid("approvalStatus") },
                "Label__Text"
              )}
            >
              Status<span className="Label__Required">*</span>
            </label>
            <Dropdown
              id="approvalStatus"
              placeholder="Select Status"
              options={approvalStatusOptions}
              name="approvalStatus"
              value={formik.values.approvalStatus}
              onChange={formik.handleChange}
              className={classNames(
                { "p-invalid": isFormFieldValid("approvalStatus") },
                "Dropdown__Round"
              )}
            />
            {getFormErrorMessage("approvalStatus")}
          </div>
        </div>
        <div className="Down__Btn p-mt-4">
          <Button
            disabled={isButtonDisabled || loading}
            label={"Submit"}
            icon={`${isButtonDisabled ? "pi pi-ban" : ""}`}
            className="Btn__Dark"
            onClick={handleButtonClick}
          />
        </div>
      </form>
    </div>
  );
};

const Input = styled.input`
  width: 40px;
  margin: 0 5px;
  text-align: center;
`;

export default DeviceManagerActionForm;
